import { Service } from 'constants/usercentrics';
import clsx from 'clsx';
import GetInTouch from 'components/Shared/GetInTouch';
import ContentBlocks from 'components/ui/ContentBlocks';
import BlockLoaderNonFields from 'components/ui/ContentBlocks/BlockLoaderNonFields';
import config from 'config';
import { useEffect } from 'react';
import { useLivePreview } from 'hooks/useLivePreview';
import type { LandingPage as LandingPageType } from 'types/LandingPage';
import type { FullWidthContentBlock } from 'types/PageData';
import '@contentful/live-preview/style.css';

type Props = {
  entry: LandingPageType;
  fullWidthBlocks: {
    top: FullWidthContentBlock | null;
    bottom: FullWidthContentBlock | null;
  };
};

const LandingPage = ({ entry, fullWidthBlocks }: Props): JSX.Element => {
  const { facebookTrackingId } = config.defaults;
  const { updatedEntity } = useLivePreview(entry);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'text/plain';
    script.dataset.usercentrics = Service.FACEBOOK_PIXEL;
    script.innerHTML = `/* Facebook Pixel Code */
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window,document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
     fbq('init', ${facebookTrackingId});
    fbq('track', 'PageView');
     /* Facebook Pixel Code */
    `;

    document.head.appendChild(script);

    return () => {
      if (script && script.parentNode?.nodeName === 'HEAD') {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <>
      <div>
        {fullWidthBlocks?.top && (
          <ContentBlocks
            type={fullWidthBlocks.top.__typename as string}
            fields={fullWidthBlocks.top}
            config={{
              bannerContainerSize: `max-w-screen-lg ${
                fullWidthBlocks.top.funnel ? 'md:-mb-56' : ''
              }`,
              isLandingPage: true,
              useH1: true,
            }}
          />
        )}

        <div
          className={clsx(
            'bg-white',
            fullWidthBlocks?.top?.funnel ? 'md:pt-32' : 'md:pt-1',
          )}
        >
          <div className="max-w-screen-lg mx-auto mb-10 lg:mb-36">
            {!entry.disableH1 && entry.title && (
              <div className="max-w-screen-xl mx-auto">
                <h1 className="text-primary text-4_3xl lg:text-5xl mb-10 mt-4 lg:mt-0 hyphenate">
                  {entry.title}
                </h1>
              </div>
            )}
            {updatedEntity.content && (
              <BlockLoaderNonFields
                entry={updatedEntity}
                field="content"
                config={{ showHeadline: true, showDivider: false }}
              />
            )}
          </div>
        </div>
        {fullWidthBlocks?.bottom && (
          <div className="mb-8">
            <ContentBlocks
              type={fullWidthBlocks.bottom.__typename as string}
              fields={fullWidthBlocks.bottom}
            />
          </div>
        )}
        {entry.showGetInTouchSection !== false && <GetInTouch />}
      </div>
      <noscript>
        <img
          height="1"
          width="1"
          src={`https://www.facebook.com/tr?id=${facebookTrackingId}&ev=PageView&noscript=1`}
          className="hidden"
        />
      </noscript>
    </>
  );
};

export default LandingPage;
